import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUp, ArrowDown, Flame, Thermometer, Droplets, Wind, Clock } from 'lucide-react';
export type ChamberType = 'Räucherkammer' | 'Heißrauch' | 'Schwitzkammer' | 'Kochkammer';
export interface ChamberBatch {
  batchNumber: string;
  productName: string;
  scannedAt: string; // ISO string
}
export interface Chamber {
  id: string;
  type: ChamberType;
  number: number;
  batches: ChamberBatch[];
}
// Generate mock chambers
const generateChambers = (): Chamber[] => {
  const types: ChamberType[] = ['Räucherkammer', 'Heißrauch', 'Schwitzkammer', 'Kochkammer'];
  const chambers: Chamber[] = [];
  const products = ['Mettenden 75g ITW', 'Käsebeisser', 'Pfefferb. pikant ITW', 'Salami Classic', 'Bierwurst'];
  types.forEach(type => {
    for (let i = 1; i <= 10; i++) {
      const isOccupied = Math.random() > 0.6;
      const chamberBatches: ChamberBatch[] = [];
      if (isOccupied) {
        // Randomly generate between 1 and 20 batches for this chamber
        const batchCount = Math.floor(Math.random() * 20) + 1;
        // Usually batches in the same chamber are the same product, but let's just pick one product for the whole chamber to be realistic
        const productName = products[Math.floor(Math.random() * products.length)];
        // Base time for the first scan
        const now = new Date();
        const basePast = new Date(now.getTime() - Math.random() * 8 * 60 * 60 * 1000);
        for (let j = 0; j < batchCount; j++) {
          // Scans happen sequentially, maybe a few seconds/minutes apart
          const scanTime = new Date(basePast.getTime() + j * Math.random() * 60000);
          chamberBatches.push({
            batchNumber: `CH-2026-05-${Math.floor(Math.random() * 900) + 100}`,
            productName,
            scannedAt: scanTime.toISOString()
          });
        }
      }
      chambers.push({
        id: `${type}-${i}`,
        type,
        number: i,
        batches: chamberBatches
      });
    }
  });
  return chambers;
};
// Static initialization so it doesn't change on every render
const initialChambers = generateChambers();
const TYPE_ICONS: Record<ChamberType, React.ElementType> = {
  Räucherkammer: Wind,
  Heißrauch: Flame,
  Schwitzkammer: Droplets,
  Kochkammer: Thermometer
};
const TYPE_COLORS: Record<ChamberType, string> = {
  Räucherkammer: 'text-amber-600 bg-amber-50 border-amber-200',
  Heißrauch: 'text-red-600 bg-red-50 border-red-200',
  Schwitzkammer: 'text-blue-600 bg-blue-50 border-blue-200',
  Kochkammer: 'text-orange-600 bg-orange-50 border-orange-200'
};
function CurrentTime() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex items-center gap-1.5 text-gray-500 font-medium ml-auto" data-id="element-12">
      <Clock className="w-4 h-4" data-id="element-13" />
      <span className="tabular-nums" data-id="element-14">
        {time.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit'
      })}{' '}
        Uhr
      </span>
    </div>;
}
function LiveTimer({
  scannedAt
}: {
  scannedAt: string;
}) {
  const [duration, setDuration] = useState('');
  useEffect(() => {
    const updateTimer = () => {
      const start = new Date(scannedAt).getTime();
      const now = new Date().getTime();
      const diff = Math.max(0, now - start);
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(diff % (1000 * 60) / 1000);
      setDuration(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [scannedAt]);
  return <span className="tabular-nums font-mono" data-id="element-15">{duration}</span>;
}
import { AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
export function ChamberView({
  onBack
}: {
  onBack: () => void;
}) {
  const categories: ChamberType[] = ['Räucherkammer', 'Heißrauch', 'Schwitzkammer', 'Kochkammer'];
  const [activeTab, setActiveTab] = useState<ChamberType>('Räucherkammer');
  const [expandedChamberId, setExpandedChamberId] = useState<string | null>(null);
  const activeChambers = initialChambers.filter(c => c.type === activeTab);
  const ActiveIcon = TYPE_ICONS[activeTab];
  const activeColorClass = TYPE_COLORS[activeTab];
  return <div className="min-h-screen bg-[#F8FAFC] font-sans text-gray-900 pb-20" data-id="element-16">
      <header className="bg-white/85 backdrop-blur-md border-b border-gray-200 sticky top-0 z-30" data-id="element-17">
        <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 mx-auto py-3 flex items-center gap-4" data-id="element-18">
          <button onClick={onBack} className="p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors" data-id="element-19">
            <ArrowLeft className="w-5 h-5" data-id="element-20" />
          </button>
          <h1 className="text-xl font-bold text-gray-900" data-id="element-21">Kammern Übersicht</h1>
          <CurrentTime data-id="element-22" />
        </div>

        {/* Tab Navigation */}
        <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 mx-auto pt-2 flex gap-6 overflow-x-auto no-scrollbar" data-id="element-23">
          {categories.map(cat => {
          const isActive = activeTab === cat;
          const CatIcon = TYPE_ICONS[cat];
          return <button key={cat} onClick={() => setActiveTab(cat)} className={`relative pb-3 flex items-center gap-2 text-sm font-semibold transition-colors whitespace-nowrap ${isActive ? 'text-[#E87722]' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-24">
                <CatIcon className="w-4 h-4" data-id="element-25" />
                {cat}
                {isActive && <motion.div layoutId="activeTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E87722]" data-id="element-26" />}
              </button>;
        })}
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 mx-auto py-8" data-id="element-27">
        <motion.section key={activeTab} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2
      }} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-28">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between" data-id="element-29">
            <div className="flex items-center gap-3" data-id="element-30">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${activeColorClass}`} data-id="element-31">
                <ActiveIcon className="w-5 h-5" data-id="element-32" />
              </div>
              <h2 className="text-lg font-bold text-gray-900" data-id="element-33">{activeTab}</h2>
              <span className="px-2.5 py-1 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold" data-id="element-34">
                {activeChambers.filter(c => c.batches.length > 0).length} / 10
                belegt
              </span>
            </div>
          </div>

          <div className="p-6 flex flex-col gap-3" data-id="element-35">
            {activeChambers.map(chamber => {
            const isOccupied = chamber.batches.length > 0;
            const isExpanded = expandedChamberId === chamber.id;
            return <motion.div layout key={chamber.id} onClick={() => {
              if (isOccupied) {
                setExpandedChamberId(isExpanded ? null : chamber.id);
              }
            }} className={`relative rounded-xl border-2 transition-all overflow-hidden ${isOccupied ? 'border-[#E87722]/30 bg-[#E87722]/5 cursor-pointer hover:border-[#E87722]/50' : 'border-gray-100 bg-gray-50/50 border-dashed'}`} data-id="element-36">
                  <motion.div layout className="p-4 flex items-center justify-between" data-id="element-37">
                    <div className="flex items-center gap-4" data-id="element-38">
                      <span className="text-sm font-bold text-gray-700" data-id="element-39">
                        Kammer {chamber.number}
                      </span>
                      {isOccupied ? <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-100" data-id="element-40">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]" data-id="element-41" />
                          Belegt
                        </span> : <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200" data-id="element-42">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" data-id="element-43" />
                          Leer
                        </span>}
                    </div>
                    
                    <div className="flex items-center gap-4" data-id="element-44">
                      {isOccupied && <span className="text-xs font-bold text-[#E87722] bg-[#E87722]/10 px-2.5 py-1 rounded-full" data-id="element-45">
                          {chamber.batches.length} / 20 Chargen
                        </span>}
                      {isOccupied && <ChevronDown className={`w-5 h-5 text-[#E87722] transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-46" />}
                    </div>
                  </motion.div>

                  <AnimatePresence data-id="element-47">
                    {isExpanded && isOccupied && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} className="border-t border-[#E87722]/20 bg-white/50" data-id="element-48">
                        <div className="p-4" data-id="element-49">
                          <table className="w-full text-left" data-id="element-50">
                            <thead data-id="element-51">
                              <tr className="text-xs font-semibold text-gray-500 border-b border-gray-200" data-id="element-52">
                                <th className="pb-2 font-medium" data-id="element-53">Charge</th>
                                <th className="pb-2 font-medium" data-id="element-54">Produkt</th>
                                <th className="pb-2 font-medium" data-id="element-55">Scan-Zeit</th>
                                <th className="pb-2 font-medium text-right" data-id="element-56">Dauer</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100" data-id="element-57">
                              {chamber.batches.map((b, idx) => <tr key={idx} className="hover:bg-white transition-colors" data-id="element-58">
                                  <td className="py-3 text-sm font-mono font-bold text-gray-900" data-id="element-59">
                                    {b.batchNumber}
                                  </td>
                                  <td className="py-3 text-sm font-medium text-gray-700" data-id="element-60">
                                    {b.productName}
                                  </td>
                                  <td className="py-3 text-sm text-gray-600" data-id="element-61">
                                    <div className="flex items-center gap-1.5" data-id="element-62">
                                      <Clock className="w-3.5 h-3.5 text-gray-400" data-id="element-63" />
                                      {new Date(b.scannedAt).toLocaleTimeString('de-DE', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })} Uhr
                                    </div>
                                  </td>
                                  <td className="py-3 text-sm font-semibold text-[#E87722] text-right" data-id="element-64">
                                    <LiveTimer scannedAt={b.scannedAt} data-id="element-65" />
                                  </td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </div>
        </motion.section>
      </main>
    </div>;
}